import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { firestore as db } from "../../FirebaseConfig";
import { useNavigate, useLocation } from "react-router-dom";

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchRecipes();
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const recipeId = queryParams.get("id");

    if (recipeId) {
      fetchRecipeDetails(recipeId);
    }
  }, [location.search, recipes]);

  // **🔹 Fetch All Recipes**
  const fetchRecipes = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "recipes"));
      const recipesArray = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      recipesArray.sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date (newest first)
      setRecipes(recipesArray);
    } catch (error) {
      console.error("Error fetching recipes:", error);
    }
    setLoading(false);
  };

  // **🔹 Fetch Recipe Details by ID**
  const fetchRecipeDetails = async (recipeId) => {
    if (!recipeId) return;

    try {
      const docRef = doc(db, "recipes", recipeId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setSelectedRecipe({ id: recipeId, ...docSnap.data() });
      } else {
        navigate("/recipes");
      }
    } catch (error) {
      console.error("Error fetching recipe:", error);
      navigate("/recipes");
    }
  };

  // **🔹 Get Next & Previous Recipes**
  const getNextPrevRecipes = () => {
    if (!selectedRecipe || recipes.length === 0) return { next: null, prev: null };

    const index = recipes.findIndex((recipe) => recipe.id === selectedRecipe.id);
    return {
      prev: index > 0 ? recipes[index - 1].id : null,
      next: index < recipes.length - 1 ? recipes[index + 1].id : null,
    };
  };

  const { next, prev } = getNextPrevRecipes();

  if (loading) {
    return <div className="h-screen flex items-center justify-center text-gray-600">Loading recipes...</div>;
  }

  // **🔹 Show Recipe List**
  if (!selectedRecipe) {
    return (
      <div className="max-w-7xl mt-27 mx-auto p-6">
        <h1 className="text-4xl font-extrabold text-gray-900 text-center">Delicious Recipes</h1>
        <p className="text-lg text-gray-600 text-center mt-2">
          Discover the best homemade recipes curated for you! 🍽️🔥
        </p>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {recipes.map((recipe) => (
            <div
              key={recipe.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden cursor-pointer transform hover:scale-105 transition-all duration-300"
              onClick={() => navigate(`/recipes?id=${recipe.id}`)}
            >
              <img src={recipe.thumbnail} alt={recipe.name} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h2 className="text-xl font-semibold text-gray-800">{recipe.name}</h2>
                <p className="text-gray-500 text-sm mt-1">{recipe.date}</p>
                <p className="text-gray-600 mt-2 text-sm">{recipe.summary}...</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // **🔹 Show Single Recipe**
  return (
    <div className="max-w-5xl mt-27 mx-auto p-6">
      <div className="bg-white shadow-lg rounded-lg p-8">
        <h1 className="text-4xl font-bold text-[#f47d02]">{selectedRecipe.name}</h1>
        <p className="text-gray-500 text-lg mt-2">{selectedRecipe.date}</p>

        {/* Image Gallery */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {selectedRecipe.images?.map((img, index) => (
            <div key={index} className="overflow-hidden rounded-lg shadow-md">
              <img
                src={img}
                alt={`Recipe Image ${index + 1}`}
                className="w-full h-56 object-cover hover:scale-110 transition-transform duration-300"
              />
            </div>
          ))}
        </div>

        {/* Recipe Content */}
        <div className="mt-6 text-gray-700 text-lg leading-relaxed px-2" dangerouslySetInnerHTML={{ __html: selectedRecipe.content }}></div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-10">
          {prev ? (
            <button
              onClick={() => navigate(`/recipes?id=${prev}`)}
              className="bg-gray-800 text-white px-5 py-3 rounded-md hover:bg-gray-900 transition-all duration-300"
            >
              ← Previous Recipe
            </button>
          ) : (
            <div></div>
          )}

          {/* <button
            onClick={() => navigate("/recipes")}
            className="bg-[#f47d02] text-white px-5 py-3 rounded-md hover:bg-orange-600 transition-all duration-300"
          >
            Back to Recipes
          </button> */}

          {next ? (
            <button
              onClick={() => navigate(`/recipes?id=${next}`)}
              className="bg-[#f47d02] text-white px-5 py-3 rounded-md hover:bg-orange-600 transition-all duration-300"
            >
              Next Recipe →
            </button>
          ) : (
            <div></div>
          )}
        </div>
      </div>
    </div>
  );
}
